import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { gql, useLazyQuery } from '@apollo/client';
import publicImgBaseURL from 'commons/publicImgBaseURL';
import getProductImageUrl from 'commons/getProductImageUrl';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import useStoreDetails from 'hooks/useStoreDetails';
import JBImage from 'components/essentials/JBImage';
import { PRODUCT_GRID_FRAGMENT_LITE } from '@/graphql/queries';
import { GET_PRODUCT_OPTIONS_LEAD_TIMES } from 'data-hooks/usePDPLeadTime/queries';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import useUserLocation from 'global-state/userLocation/useUserLocation';
const GET_PRODUCT_MINIMAL_LAYOUT_WITH_QSSTOCK = gql`
  query getProduct(
    $slug: String
    $id: Int
    $zipCode: String
    $regionId: String
    $stateCode: String
    $fromCache: Boolean
    $productSku: String
  ) {
    product: getProduct(slug: $slug, id: $id, productSku: $productSku) {
      id
      sku
      slug
      name
      default_option_value
      default_option_image
      price
      efg_flag
      is_external
      upholstery_product {
        attributes_min {
          urlKey
        }
      }
      qs_stock(zipCode: $zipCode, fromCache: $fromCache) {
        stock
        errors {
          message
        }
      }
      attributes {
        code
        label
        name
        value
      }
      hero_images_min {
        sort_order
        url
        img {
          url
          cropData
        }
      }
      images {
        sort_order
        url
      }
      family {
        name
      }
      type {
        name
      }
      meta {
        id
        active
        inventory_count
        manufacturer
        config {
          fabric_count
          has_cushion
          has_mattress
          has_orientation
          stain_count
        }
        customer {
          favorited
          favOptions {
            optionName
            optionId
            optionType
          }
          is_designer
        }
        price(stateCode: $stateCode, regionId: $regionId) {
          discount_percent
          final_price
          financing_month
          original_price
          max_discount_percent
        }
        promotion {
          active
          template
          type
          endDate
          discount
        }
        shipping {
          lead_time
          quick_ship
          variants {
            id
            lead_time
            bucket_lead_time
            qty
            quick_ship
            variant
            title
            description
            icon
            image
            sku
          }
        }
      }
      ...ProductGridFragmentLite
    }
  }

  ${PRODUCT_GRID_FRAGMENT_LITE}
`;
const ProductCatalogCard = props => {
  const $ = _c(75);
  const {
    product
  } = props;
  const [zipCode, setZipCode] = useState(null);
  const storeData = useStoreDetails();
  const [productImgUrl, setProductImgUrl] = useState(storeData?.zipCode);
  const [optionsParams, setOptionsParams] = useState(null);
  const userLocation = useUserLocation();
  const t0 = parseInt(product?.product_id, 10);
  const t1 = userLocation?.stateShortName;
  let t2;
  if ($[0] !== t1) {
    t2 = getVisitorLocationQueryVariables(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t0 || $[3] !== t2 || $[4] !== zipCode) {
    t3 = {
      id: t0,
      zipCode,
      ...t2
    };
    $[2] = t0;
    $[3] = t2;
    $[4] = zipCode;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      noBatch: true
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = {
      variables: t3,
      context: t4
    };
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: productData
  } = useSuspenseQuery(GET_PRODUCT_MINIMAL_LAYOUT_WITH_QSSTOCK, t5);
  const [getProductLeadTimes, t6] = useLazyQuery(GET_PRODUCT_OPTIONS_LEAD_TIMES);
  const {
    loading: leadTimeLoading,
    data: leadTimeData
  } = t6;
  const isDiscontinuedProduct = product.visibility === 0 && product.item_availability === 2;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = productInfo => {
      if (isEmpty(productInfo.product.options)) {
        setOptionsParams({});
        return;
      }
      const {
        options
      } = productInfo.product;
      const optionsObj = options.reduce(_temp, {});
      setOptionsParams(optionsObj);
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const setDefaultOptionValues = t7;
  let t8;
  if ($[10] !== userLocation?.zip) {
    t8 = () => {
      if (userLocation?.zip) {
        setZipCode(userLocation?.zip);
      }
    };
    $[10] = userLocation?.zip;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = userLocation?.zip;
  let t10;
  if ($[12] !== t9) {
    t10 = [t9];
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  useEffect(t8, t10);
  let t11;
  if ($[14] !== getProductLeadTimes || $[15] !== isDiscontinuedProduct || $[16] !== optionsParams || $[17] !== product.product_id || $[18] !== zipCode) {
    t11 = () => {
      if (!product?.product_id || isDiscontinuedProduct || !optionsParams) {
        return;
      }
      getProductLeadTimes({
        variables: {
          id: parseInt(product.product_id, 10),
          zipCode,
          options: JSON.stringify(optionsParams),
          noCache: true
        }
      });
    };
    $[14] = getProductLeadTimes;
    $[15] = isDiscontinuedProduct;
    $[16] = optionsParams;
    $[17] = product.product_id;
    $[18] = zipCode;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = userLocation?.zip;
  const t13 = product?.product_id;
  let t14;
  if ($[20] !== getProductLeadTimes || $[21] !== isDiscontinuedProduct || $[22] !== optionsParams || $[23] !== t12 || $[24] !== t13 || $[25] !== zipCode) {
    t14 = [optionsParams, t12, getProductLeadTimes, isDiscontinuedProduct, t13, zipCode];
    $[20] = getProductLeadTimes;
    $[21] = isDiscontinuedProduct;
    $[22] = optionsParams;
    $[23] = t12;
    $[24] = t13;
    $[25] = zipCode;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  useEffect(t11, t14);
  let t15;
  let t16;
  if ($[27] !== productData) {
    t15 = () => {
      if (!productData?.product?.name) {
        return;
      }
      setDefaultOptionValues(productData);
      setProductImgUrl(getProductImageUrl(productData.product));
    };
    t16 = [productData];
    $[27] = productData;
    $[28] = t15;
    $[29] = t16;
  } else {
    t15 = $[28];
    t16 = $[29];
  }
  useEffect(t15, t16);
  const qsStock = productData?.product?.qs_stock?.stock?.[`${product?.product_id}`];
  let stockQuantity;
  if ($[30] !== isDiscontinuedProduct || $[31] !== qsStock) {
    stockQuantity = 0;
    if (!isDiscontinuedProduct && qsStock) {
      Object.entries(qsStock).forEach(t17 => {
        const [, stockObject] = t17;
        if (stockObject.stock > 0) {
          stockQuantity = stockQuantity + stockObject.stock;
          stockQuantity;
        }
      });
    }
    $[30] = isDiscontinuedProduct;
    $[31] = qsStock;
    $[32] = stockQuantity;
  } else {
    stockQuantity = $[32];
  }
  let t17;
  if ($[33] !== product.small_img) {
    t17 = product.small_img ? publicImgBaseURL(product.small_img) : "";
    $[33] = product.small_img;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const imgSrc = t17;
  const constructedImgSrc = parseInt(productData?.product?.id, 10) === product.product_id ? product.option_name ? productImgUrl?.transparent_config_image : productImgUrl?.transparent_hero_image : "";
  const fallbackImgSrc = parseInt(productData?.product?.id, 10) === product.product_id ? product.option_name ? productImgUrl?.transparent_config_image_ts : productImgUrl?.transparent_hero_image_ts : "";
  let t18;
  if ($[35] !== product.price || $[36] !== productData) {
    t18 = () => {
      if (!productData?.product) {
        return <span className="text-base font-bold text-gray before:[content:'$']">{product.price}</span>;
      }
      const originalPrice = productData?.product?.price || product.price;
      let maxDiscountedAmount = 0;
      let discountedAmount = 0;
      if (productData.product.meta?.price?.max_discount_percent) {
        maxDiscountedAmount = originalPrice - Math.round(originalPrice * (parseFloat(productData.product.meta.price.max_discount_percent) / 100));
      }
      if (productData.product.meta?.price?.discount_percent) {
        discountedAmount = originalPrice - Math.round(originalPrice * (parseFloat(productData.product.meta.price.discount_percent) / 100));
      }
      if (discountedAmount && maxDiscountedAmount && maxDiscountedAmount !== discountedAmount) {
        return <><span className="text-base font-bold text-gray before:[content:'$'] line-through">{originalPrice}</span>{" "}<span className="text-base font-bold before:[content:'$'] text-red">{maxDiscountedAmount}</span>{" - "}<span className="text-base font-bold before:[content:'$'] text-red">{discountedAmount}</span></>;
      }
      if (discountedAmount) {
        return <><span className="text-base font-bold text-gray before:[content:'$'] line-through">{originalPrice}</span>{" "}<span className="text-base font-bold before:[content:'$'] text-red">{discountedAmount}</span></>;
      }
      return <span className="text-base font-bold text-gray before:[content:'$']">{originalPrice}</span>;
    };
    $[35] = product.price;
    $[36] = productData;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const showPrice = t18;
  const t19 = product.product_id;
  const t20 = constructedImgSrc || fallbackImgSrc || imgSrc;
  const t21 = fallbackImgSrc || imgSrc;
  let t22;
  if ($[38] !== imgSrc || $[39] !== t20 || $[40] !== t21) {
    t22 = <JBImage src={t20} fallbackSrc={t21} fallbackSrc2={imgSrc} className="opacity-100 transition-[opacity_1s] object-contain max-w-[12.5rem] h-28 min-h-[7rem] mr-[1.2rem] min-w-[13rem]" loader={true} width={368} trim="color" trimPad={20} />;
    $[38] = imgSrc;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const t23 = product.name || "";
  let t24;
  if ($[42] !== t23) {
    t24 = <div className="text-base font-bold text-gray">{t23}</div>;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== showPrice) {
    t25 = showPrice();
    $[44] = showPrice;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t24 || $[47] !== t25) {
    t26 = <div className="w-full max-[1023px]:w-[unset]">{t24}{t25}</div>;
    $[46] = t24;
    $[47] = t25;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t22 || $[50] !== t26) {
    t27 = <div className="flex items-center w-[26rem] min-w-[26rem] mr-[1.2rem] text-gray max-[1023px]:min-w-[unset] max-[1023px]:w-[unset] max-[1023px]:flex-col max-[1023px]:items-center">{t22}{t26}</div>;
    $[49] = t22;
    $[50] = t26;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span className="lg:hidden"> </span>;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== product.serial_number) {
    t29 = <span className="text-base w-full ml-[1.2rem] text-gray max-[1023px]:ml-0 before:max-[1023px]:[content:'SKU:_']">{" "}{product.serial_number}</span>;
    $[53] = product.serial_number;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  const t30 = isDiscontinuedProduct ? " - " : leadTimeData?.getProductOptionsLeadTime ? `${Math.ceil((leadTimeData?.getProductOptionsLeadTime?.lead_time || 0) / 7)} weeks` : leadTimeLoading ? "Loading..." : "";
  let t31;
  if ($[55] !== t30) {
    t31 = <span className="text-base w-full text-gray before:max-[1023px]:[content:'LeadTime:_']">{t30}{" "}</span>;
    $[55] = t30;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== isDiscontinuedProduct || $[58] !== stockQuantity) {
    t32 = <span className="text-base w-full font-bold text-gray before:max-[1023px]:[content:'Stock:_']">{isDiscontinuedProduct ? <span className="text-red"> Discontinued product </span> : stockQuantity ? <span className="text-brand">{" "}{`Ready to ship (${stockQuantity})`}</span> : <span className="text-red">Out of stock</span>}</span>;
    $[57] = isDiscontinuedProduct;
    $[58] = stockQuantity;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <span className="lg:hidden"> </span>;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== t29 || $[62] !== t31 || $[63] !== t32) {
    t34 = <div className="max-[1023px]:flex max-[1023px]:flex-col max-[1023px]:min-w-[11rem]">{t28}{t29}{t31}{t32}{t33}</div>;
    $[61] = t29;
    $[62] = t31;
    $[63] = t32;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== product.product_id || $[66] !== t27 || $[67] !== t34) {
    t35 = <div key={t19} className="flex flex-row items-center w-full mx-8 my-4 p-5 h-[150px] [border-bottom:1px_solid_#e5e5e5] justify-start max-[1023px]:px-4 max-[1023px]:py-12 max-[1023px]:m-0 max-[1023px]:h-[250px] max-[1023px]:justify-between">{t27}{t34}</div>;
    $[65] = product.product_id;
    $[66] = t27;
    $[67] = t34;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  const content = t35;
  let t36;
  if ($[69] !== content || $[70] !== isDiscontinuedProduct || $[71] !== product) {
    t36 = product ? isDiscontinuedProduct || !product.url_key ? content : <Link to={`${product.url_key}`} className="no-underline [&_a]:no-underline [&_a:active]:no-underline [&_a:hover]:no-underline [&_a:link]:[&:link]:no-underline [&_a:visited]:no-underline">{content}</Link> : null;
    $[69] = content;
    $[70] = isDiscontinuedProduct;
    $[71] = product;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  let t37;
  if ($[73] !== t36) {
    t37 = <div className="product-catalog-card">{t36}</div>;
    $[73] = t36;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  return t37;
};
ProductCatalogCard.propTypes = {
  product: PropTypes.objectOf(PropTypes.object)
};
export default ProductCatalogCard;
function _temp(finalObj, curr) {
  if (curr.default_value) {
    finalObj[curr.id] = [parseInt(curr.default_value, 10)];
  } else {
    finalObj[curr.id] = [parseInt(curr.values[0].option_values[0].id, 10)];
  }
  return finalObj;
}